<template lang="pug">
v-menu(v-model="show", :close-on-content-click="false", min-width="290px", max-width="290px")
  //- DISPLAY UI INPUT
  template(v-slot:activator="{ on, attrs }")
    slot(name="field")
      v-text-field(
        :value="datetimeUiValue", 
        v-on="on", 
        readonly, 
        :label="label", 
        v-bind="{ ...textFieldProps, disabled }",
        placeholder=" "
      )
  div
    v-tabs(
      v-model="tab",
      fixed-tabs,
      color="primaryHover",
    )
      //- tab titles
      v-tab(v-if="pickerType!='Time'") Date
      v-tab(v-if="pickerType!='Date' && !disableTime") Time
      //- tab contents
      v-tab-item(v-if="pickerType!='Time'")
        //- DATE PICKER
        v-date-picker.g-date-picker(
          v-model="localDate",
          color="primaryHover",
          elevation="0",
          :show-current="false",
          :title-date-format="dateFormatter",
          :header-date-format="calendarHeaderFormatter"
        )
        //- TIME PICKER
      v-tab-item(v-if="!disableTime && pickerType!='Date'")
        g-time-picker.pb-5(
          v-model="localTime", 
          :localDate="validDate", 
          :offset.sync="actualOffset"
        )
    .px-5.pb-5.d-flex.justify-space-between.white
      //- BUTTON - CLEAR DATE
      v-btn.pa-0(
        @click="tab = 0; localDate = null; localTime = null; $emit('input', null); show = false",
        outlined,
        min-width="30",
        height="30",
        color="error",
      )
        v-icon(small) mdi-eraser
      //- BUTTON - go next (when you are in date and you should select a time too).
      v-btn.pa-0(
        color="primaryHover",
        min-width="30",
        height="30",
        @click="tab = 1" v-if="tab == 0 && !hasPickerType && !disableTime"
      )
        v-icon(color="white", small) mdi-arrow-right
      //- BUTTON - SUBMIT (when you are in time or when you have not time to select).
      v-btn.pa-0(
        color="success",
        min-width="50",
        height="30",
        @click="submit()", v-if="tab == 1 || hasPickerType || disableTime",
      ) Ok
</template>

<style lang="stylus" scoped>
.g-date-picker {
  ::v-deep .v-date-picker-title__date {
    font-size: 24px;
    font-family: "Avenir", sans-serif;
    text-align: center;
  }
}

</style>

<script>
import moment from 'moment-timezone'

export default {
  props: {
    //Use this prop if you require to pass props to the text field used for input 
    textFieldProps: {
      type: Object,
      default: function () { return {}; }
    },
    //How date pickers text-fields are gonna display the selected date
    dateFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    //How time pickers text-fields are gonna display the selected time
    timeFormat: {
      type: String,
      default: 'HH:mm'
    },
    //How the date header of the Timepicker is gonna be formatted
    uiDateFormat: {
      type: String,
      default: 'DD-MMM-YYYY'
    },
    //If requires to disable time from picker. Use it for date only fields.
    disableTime: {
      type: Boolean,
      default: false
    },
    //The value coming from the parent. Contains a full datetime value that's used for rendering the date header on time picker.
    externalValue: {
      type: String,
      default: ''
    },
    //The label will be displayed on the datetime picker header.
    label: {
      type: String,
      default: ''
    },
    //The provided value for the componente (POSSIBLY NOT IN USE ANYMORE)
    value: {
      default: ''
    },
    //True if the field is read-only.
    disabled: {
      type: Boolean,
      default: false,
    },
    //The provided timezone for the picker. Since timezones offsets update on July 2022, possibly deprecated and NOT IN USE ANYMORE.
    tz: {
      type: String,
      default: ''
    },
    //String containing the type of picker to be used. Options: null (for ExtraFields), "Time" or "Date" for native Pickup/Delivery values.
    pickerType: {
      type: String,
      default: ''
    },
    //Full value of the datetime object to be used.
    fullValue: {
      type: String,
      default: ''
    },
    //The provided existing offset to be selected on the component. 
    offset: {
      type: String,
      default: null
    },
  },
  data: function () {
    return {
      tab: 0,
      show: false,
      localDate: null,
      localTime: null,
      calculatedOffset: null
    }
  },
  computed: {
    /*
    [COMPUTED] Returns a string containing the "full format" expected for datetime fields. This is including the
    date part and the time part. As today, should return the string "DD-MMM-YYYY HH:mm".
    To change this, change the uiDateFormat prop and the timeFormat prop default values. 
    */
    fullFormat() {
      return `${this.uiDateFormat} ${this.timeFormat}`;
    },
     /*
    [COMPUTED] Returns a format string according the picker's type. 
    This is used to indicate how to format values.
    */
    chosenFormat() {
      switch(this.pickerType) {
        case 'Date':
          return this.dateFormat;
        case 'Time':
          return this.timeFormat;
        default: 
          return this.fullFormat;
      }
    },
    datetimeUiValue() {
      if(this.hasPickerType && !this.datetimeIsEmpty){
        return this.valueMoment.format(this.chosenFormat);
      }
      else if(!this.hasPickerType && this.isValidDate && this.valueMoment && !this.datetimeIsEmpty)
      {
        return this.disableTime ? this.valueMoment.format(this.uiDateFormat) : this.valueMoment.format(this.fullFormat);
      }
      else{
        return this.datetimeIsEmpty ? '' : this.invalidDatetimeMessage;
      }
    },
    //- Used to send a date to time picker in order to display in header.
    timepickerDate() {
      if(_.isEmpty(this.externalValue)) {
        return this.localDate;
      }
      else {
        return this.externalValue;
      }
    },
    //- TIMEZONES
    userTz(){
      let storeTZ = _.get(this.$store.state.user, 'user.ianaTimeZone');
      return _.isEmpty(storeTZ) ? 'Etc/GMT' : storeTZ;
    },
    actualTZ(){
      return _.isEmpty(this.tz) ? this.userTz : this.tz;
    },
    actualOffset: {
      get() {
        return this.calculatedOffset;
      },
      set(value) {
        this.calculatedOffset = value;
      }
    },
    /*
    [COMPUTED] Returns a string containing the user's timezone offset. For this, it gets the user timezone from
    the computed above, then using moment gets the UTC Offset.
    NOTE: depends on the output of the computed userTz for working properly. The fallback will provide +00:00.
    */
    userTimezoneOffset() {
      // Get the offset using moment. The result is an integer with the minutes of offset.
      const tzOffset = moment()._offset;
      if(tzOffset) {
        /*
        GENERAL NOTE: Once it gets the offset, the minutes and hours are calculated using absolute values
        since this offset can be negative. This won't affect the offset symbol because the step 5, but for
        the calculation of minutes where we need to substract (get the difference), it helps to use the
        absolute values and remove ambiguities from negative values.
        */
        //1. Get the hours dividing the offset by 60 and rounding it to integers only.
        const hours = Math.abs(Math.round(parseInt(tzOffset) / 60));
        //2. Get the minutes by substracting the resulting hours (multiplied by 60 to "convert" them to minutes)
        const minutes = (Math.abs(tzOffset) - (60 * hours));
        //3. Build a valid hours string by adding a 0 before the hours if is one-digit long (less than 10).
        const hoursString = hours > 9 ? hours : "0" + hours;
        //4. Build a valid minutes string by adding a 0 before the minutes if is one-digit long (less than 10).
        const minutesString = minutes > 9 ? minutes : "0" + minutes;
        //5. Calculate the symbol. If is negative, use the minus, if not, use the plus (including for 0).
        const symbol = tzOffset >= 0 ? "+" : "-";

        //6. Return the build string by using the valid strings.
        //   You should expect output like the following examples:
        //   +00:00 | -02:00  | +11:45  | etc...
        return symbol + hoursString + ":" + minutesString;
      }
      else return "+00:00";       // Fallback: return an UTC offset (+00:00)
    },
    /*
    [COMPUTED] Return a moment object created using the ValidDate and validTime variables.
    When you require to format or use moment objects, refer to this one as starting point since it uses the
    actual values from this component. To update this output, update validDate or validTime variables.
    */
    valueMoment() {
        return moment(`${this.validDate} ${this.validTime}`);
    },
    /*
    [COMPUTED] Returns a boolean indicating if the valuemoment computed from above is valid or not.
    Useful for avoiding UI breakups because of incomplete or wrong datetime inputs.
    */
    isValidDate() {
      return (!this.datetimeIsEmpty && this.valueMoment && this.valueMoment.isValid());
    },
    /*
    [COMPUTED] Returns a boolean indicating if the component has a picker Type provided as prop.
    If there's a picker type, it means this DateTimePicker component is part of the DateRangePicker, used as
    Pickup and Delivery Dates for Orders, Shipments or Order lines. If not, it means is a criteria or an 
    Extra Field which holds the date and the time values as the same selector.
    */
    hasPickerType() {
      return !_.isEmpty(this.pickerType);
    },
    /*
    [COMPUTED] Returns the message to be displayed at the UI input when an invalid date is provided.
    */
    invalidDatetimeMessage()
    {
      return this.datetimeIsEmpty ? '' : 'Invalid Date';
    },
    /*
    [COMPUTED] Returns a boolean that indicates if both the time and date are empty. 
    */
    datetimeIsEmpty(){
      return _.isEmpty(this.localTime) && _.isEmpty(this.localDate);
    },
    /*
    [COMPUTED] Returns a valid date string created from the localDate variable and formatted with the 
    dateFormat variable (YYYY-MM-DD). This can be changed at the props default value for that variable.
    */
    validDate() {
      if(!_.isEmpty(this.localDate))
        return this.localDate;
      else if(!_.isEmpty(this.externalValue))
        return moment(this.externalValue).format(this.dateFormat);
      else
        return moment().format(this.dateFormat);
    },
    /*
    [COMPUTED] Returns a valid time string created from the localTime variable and formatted with the 
    timeFormat variable (HH:mm). This can be changed at the props default value for that variable.
    */
    validTime() {
      if(!_.isEmpty(this.localTime) && this.localTime != 'Invalid date')
        return this.localTime;
      else if(!_.isEmpty(this.externalValue))
        return moment(this.externalValue).format(this.timeFormat); 
      else
        return '00:00'; 
    },
    /*
    [COMPUTED] Returns a boolean indicating if the offset substring exists at the end of the component's value.
    For this, uses a Regex looking for this:     +/-00:00 <- can be any numbers and one plus or minus sign
    */
    hasOffsetRegexAtEnd() {
      const regex = /[+-][0-9]{2}:[0-9]{2}/;
      return regex.test(this.value.substring(this.value.length -6, this.value.length));
    },
  },
  watch: {
    /*
    [WATCHER] If the localDate changes, and the picker is type 'Date', emit this new value of localDate.
    */
    localDate(newValue) {
      if(this.pickerType === 'Date') this.$emit('input', newValue);
    },
     /*
    [WATCHER] If the localTime changes, and the picker is type 'Time', emit this new value of localTime.
    */
    localTime(newValue) {
      if(this.pickerType === 'Time') this.$emit('input', newValue);
    },
     /*
    [WATCHER] If the DateTimePicker component value changes, assign the value to the localDate and localTime
    variables according the type of picker using moment objects parsed as strings.
    NOTE: be careful to comply with standards when sending the datetime strings to momentJS. 
    - Accepted formats: ISO8601 &  RFC2822.
    */
    value: {
      immediate: true,
      handler(val) {
        if(this.hasPickerType && !_.isEmpty(val) && val.isValid) {
          this.localDate = val.format(this.dateFormat);
          this.localTime = val.format(this.timeFormat);
        }
        else if(!this.hasPickerType && !_.isEmpty(val)) {
          const momentVal = moment.parseZone(val);
          this.localDate = momentVal.format(this.dateFormat);
          this.localTime = momentVal.format(this.timeFormat);
        }
        else if(_.isEmpty(val)) {
          this.localDate = null;
          this.localTime = null;
        }
      }
    },
     /*
    [WATCHER] Once the ActualOffset changes, emit the value to the parent. In the native fields, is to send the
    value to the DateRangePicker.
    */
    actualOffset: {
      immediate: true,
      handler(newOffset) {
        if(newOffset) this.$emit('update:offset', newOffset);
      }
    },
    /*
    [WATCHER] If the externalValue changes or is provided, then calculate the localDate and the 
    localTime from it. It works only on specific pickers (Time or date), which are the ones used
    inside the DateRangePicker component. If the picker is an ExtraField, it does nothing.
    */
    externalValue: {
        immediate: true,
        handler(val) {
          if(val && this.hasPickerType){
            if(this.pickerType != 'Date' || this.localDate == null) this.localDate = moment(val).format(this.dateFormat);
            if(this.pickerType != 'Time' || this.localTime == null) this.localTime = moment(val).format(this.timeFormat);
          }
        }
    }
  },
  methods: {
    submit () {
      //If has picker type, then is intended to be a simple hour or date picker.
      //Emit in the format of the type only. (YYYY-MM-DD or HH:mm)
      if(this.hasPickerType)
      {
          const finalValue = moment(`${this.validDate} ${this.validTime}`).format(this.chosenFormat); 
          if(finalValue) this.$emit('input', finalValue);
      }
      //If has no picker type, is a simple selector for Extra Data or Criteria. Emit in format DateTimeOffset:
      // YYYY-MM-DD HH:mm +HH:mm
      else
      {
          this.$emit('input', `${this.localDate}T${this.localTime || "00:00"}:00${this.actualOffset}`);
      }
      //Hide modal and reset tab   
      this.show = false;
      this.tab = 0;
    },
    
    /*
    Format the date from the value for UI display in the header of the pickup modal 
    showing something like:    Mon, June 20
    -----------------------------------------
    Parameter: val - the input datetime string.
    returns: the value formatted as ddd, MMMM DD
    */
    dateFormatter(val) {
      return moment(val).format("ddd, MMMM DD");;
    },

    /*
    Format the date input as full month name, used for display purposes on the pickup of date.
    -------------------------------------------
    Parameter: val - the input datetime string.
    returns: the value formatted as full month name
    */
    calendarHeaderFormatter: (dateInput) => {
      return moment(dateInput).format('MMMM');
    },
    
  },
  mounted() {
      if(this.offset) {
        this.calculatedOffset = this.offset;
      } 
      else if(!this.offset && !this.hasPickerType && this.value && this.hasOffsetRegexAtEnd)
      {
        this.calculatedOffset = this.value.substring(this.value.length -6, this.value.length);
      }
      else if(!this.offset && !this.value)
      {
        this.calculatedOffset = this.userTimezoneOffset;
      }
      else
      {
        this.calculatedOffset = "+00:00";
      }
  }
}
</script>
